import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "../api";
import { useMember } from "../MemberContext";
import { ChipSelect, H1, Overline, TIER_LABEL, TierBadge } from "../ui";

const DAY_LABEL = { mo: "Mo", di: "Di", mi: "Mi", do: "Do", fr: "Fr", sa: "Sa", so: "So" };
const Block = ({ title, children }) => <div className="border-t border-white/10 py-8 grid md:grid-cols-3 gap-6"><p className="text-xs uppercase tracking-[0.2em] text-[#71717A]">{title}</p><div className="md:col-span-2 space-y-4">{children}</div></div>;

function Privacy() {
  const { setMember } = useMember();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [ask, setAsk] = useState(false);
  const exportData = async () => {
    try {
      const d = await api("/me/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" }));
      Object.assign(document.createElement("a"), { href: url, download: "meine-trainingsdaten.json" }).click();
    } catch (e) { toast.error(e.message); }
  };
  const del = async () => {
    try { await api("/me/delete", { method: "POST", body: { password: pw } }); setMember(false); toast.success("Dein Konto wurde gelöscht"); nav("/training"); } catch (e) { toast.error(e.message); }
  };
  return (
    <Block title="Datenschutz">
      <button onClick={exportData} className="flex items-center gap-2 text-sm text-white border border-white/15 px-4 h-11" data-testid="profile-export"><Download className="w-4 h-4" />Meine Daten herunterladen</button>
      {!ask ? <button onClick={() => setAsk(true)} className="flex items-center gap-2 text-sm text-red-300" data-testid="profile-delete-start"><Trash2 className="w-4 h-4" />Konto löschen</button> : (
        <div className="border border-red-500/40 p-4 space-y-3" data-testid="profile-delete-box">
          <p className="text-sm text-[#A1A1AA]">Alle Profil-, Favoriten- und Trainingsdaten werden endgültig gelöscht. Bitte bestätige mit deinem Passwort.</p>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 bg-transparent border-white/15 rounded-none" data-testid="profile-delete-password" />
          <button onClick={del} disabled={!pw} className="h-11 px-5 bg-red-600 text-white text-sm disabled:opacity-50" data-testid="profile-delete-confirm">Endgültig löschen</button>
        </div>
      )}
    </Block>
  );
}

export default function Profile() {
  const { member, setMember, meta, logout } = useMember();
  const [f, setF] = useState(member);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  if (!meta) return null;
  const save = async () => {
    setBusy(true);
    try {
      const body = ["first_name", "last_name", "position", "level", "club", "goals", "training_days", "session_minutes", "equipment", "notification_prefs"].reduce((a, k) => ({ ...a, [k]: f[k] }), {});
      setMember((await api("/me/profile", { method: "PUT", body })).member); toast.success("Profil gespeichert");
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const prefs = f.notification_prefs || {};
  return (
    <div data-testid="profile-page" className="max-w-4xl">
      <Overline className="mb-3">Profil</Overline>
      <H1 className="mb-4 !text-5xl sm:!text-6xl">{member.first_name} {member.last_name}</H1>
      <div className="flex items-center gap-3 mb-10 text-sm text-[#A1A1AA]"><span data-testid="profile-tier">Mitgliedschaft: {TIER_LABEL[member.tier]}</span><TierBadge tier={member.tier} /></div>
      <Block title="Name & Verein">
        <div className="grid grid-cols-2 gap-3">
          <Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-11 bg-transparent border-white/15 rounded-none" data-testid="profile-first-name" />
          <Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-11 bg-transparent border-white/15 rounded-none" data-testid="profile-last-name" />
        </div>
        <Input value={f.club || ""} onChange={(e) => set("club", e.target.value)} placeholder="Verein (optional)" className="h-11 bg-transparent border-white/15 rounded-none" data-testid="profile-club" />
      </Block>
      <Block title="Position"><ChipSelect options={meta.positions} value={f.position} onChange={(v) => set("position", v)} testPrefix="profile-position" /></Block>
      <Block title="Niveau"><ChipSelect options={meta.levels} value={f.level} onChange={(v) => set("level", v)} testPrefix="profile-level" /></Block>
      <Block title="Ziele"><ChipSelect multi max={3} options={meta.goals} value={f.goals} onChange={(v) => set("goals", v)} testPrefix="profile-goal" /></Block>
      <Block title="Trainingstage"><ChipSelect multi options={meta.weekdays.map((d) => ({ id: d, label: DAY_LABEL[d] }))} value={f.training_days} onChange={(v) => set("training_days", v)} testPrefix="profile-day" /></Block>
      <Block title="Zeit pro Einheit"><ChipSelect options={meta.durations.map((d) => ({ id: d, label: `${d} Min` }))} value={f.session_minutes} onChange={(v) => set("session_minutes", v)} testPrefix="profile-duration" /></Block>
      <Block title="Equipment"><ChipSelect multi options={meta.equipment.filter((e) => e.id !== "keins")} value={f.equipment} onChange={(v) => set("equipment", v)} testPrefix="profile-equipment" /></Block>
      <Block title="Benachrichtigungen">
        <label className="flex items-center justify-between text-sm text-white">Neuigkeiten & Trainingstipps per E-Mail<Switch checked={!!prefs.email_news} onCheckedChange={(v) => set("notification_prefs", { ...prefs, email_news: v })} data-testid="profile-pref-email" /></label>
        <label className="flex items-center justify-between text-sm text-white">Neue Live-Trainings per E-Mail<Switch checked={!!prefs.live} onCheckedChange={(v) => set("notification_prefs", { ...prefs, live: v })} data-testid="profile-pref-live" /></label>
        <label className="flex items-center justify-between text-sm text-white">Neue Challenges per E-Mail<Switch checked={!!prefs.challenges} onCheckedChange={(v) => set("notification_prefs", { ...prefs, challenges: v })} data-testid="profile-pref-challenges" /></label>
        <p className="text-xs text-[#71717A]">Standardmäßig ausgeschaltet. Wichtige E-Mails (Passwort, Zahlungen, Erinnerung an gebuchte Live-Trainings, Absagen) erhältst du immer.</p>
      </Block>
      <div className="flex gap-3 py-8 border-t border-white/10">
        <button disabled={busy} onClick={save} className="h-12 px-8 bg-[#c1ff72] text-black font-bold uppercase tracking-wider" data-testid="profile-save">Speichern</button>
        <button onClick={logout} className="h-12 px-6 border border-white/15 text-white md:hidden" data-testid="profile-logout">Abmelden</button>
      </div>
      <Privacy />
    </div>
  );
}
